import { CheckboxButtonGroupValue } from "./Button";
import { TEXT_TAGS, TextTag } from "./CardAbility";
import { CardElementName } from "./CardElement";
import { FoilType } from "./CardFinish";
import { CardRarity, CardSerialType } from "./CardRarity";
import { CardRuleSet } from "./CardRuleEngine";
import { CardVariantName } from "./CardVariant";
import { OpusString, opusIdToString, opusStringToId } from "./Opus";

export const artTypes = ["base", "promo", "alternate"] as const;
export type ArtType = typeof artTypes[number];
export const cardTypes = ["Forward", "Backup", "Monster", "Summon"] as const;
export type CardType = typeof cardTypes[number];
export const cardFlagTypes = ["LB", "EX", "Multi"] as const;
export type CardFlagType = typeof cardFlagTypes[number];

export const CARD_BOOLEAN_PROPERTIES = ["isExBurst", "isMultiPlayable", "isSpecial", "isLB"] as const;
export const CARD_STRING_PROPERTIES = ["serial", "name", "text", "rarity", "type"] as const;
export const CARD_STRING_ARRAY_PROPERTIES = ["categories", "jobs", "elements"] as const;
export const CARD_NUMBER_PROPERTIES = ["opus", "number", "power", "cost"] as const;

export type CardBooleanProperty = typeof CARD_BOOLEAN_PROPERTIES[number];
export type CardStringProperty = typeof CARD_STRING_PROPERTIES[number];
export type CardStringArrayProperty = typeof CARD_STRING_ARRAY_PROPERTIES[number];
export type CardNumberProperty = typeof CARD_NUMBER_PROPERTIES[number];

export type DbCardVariant = {
    id: string,
    serial: string,
    altSerial: string,
    printedSerial: string,
    name: string,
    opus: number,
    altOpus: number | null,
    number: number,
    text: string,
    power: number,
    categories: string[],
    jobs: string[],
    rarity: CardRarity | "",
    cost: number,
    elements: CardElementName[],
    type: CardType | "",
    isExBurst: boolean,
    isMultiPlayable: boolean,
    isSpecial: boolean,
    isLB: boolean,

    isPrimaryVariant: boolean,
    imagePath: string,
    isTempImage: boolean,
    finish: FoilType,
    variant: CardVariantName,
    octgnId: string,
    tcgPlayerId: number,
    products: number[],
    artists: string[],

    rules?: CardRuleSet[],
    isSearcher?: boolean
}

export type CardJsonV2 = {
    serial: string,
    name: string,
    opus: number,
    number: number,
    text: string,
    power: number,
    categories: string[],
    jobs: string[],
    rarity: CardRarity | "",
    cost: number,
    elements: CardElementName[],
    type: CardType | "",
    isExBurst: boolean,
    isMultiPlayable: boolean,
    isSpecial: boolean,
    isLB: boolean,
    imageId: string,
    isSearcher?: boolean
}


export function opusNumberToOpusString(opus: number): string | null {
    const match = opusIdToString[opus];
    return match ? match : opus ? opus.toString() : null;
}

export function opusStringToOpusNumber(opus: string): number {
    const match = opusStringToId[opus as OpusString];
    const parsedOpus = parseInt(opus);
    return match ? match : isNaN(parsedOpus) ? 0 : parsedOpus;
}

export function getDefaultDbVariant(props?: { isPrimaryVariant?: boolean }): DbCardVariant {
    return {
        categories: [],
        cost: 0,
        elements: [],
        isExBurst: false,
        isLB: false,
        isMultiPlayable: false,
        isSpecial: false,
        jobs: [],
        name: "",
        number: 0,
        opus: 0,
        altOpus: null,
        power: 0,
        rarity: "",
        id: "",
        text: "",
        type: "",

        printedSerial: "",
        altSerial: "",
        artists: [],
        finish: "base",
        imagePath: "",
        isPrimaryVariant: !!props?.isPrimaryVariant,
        isTempImage: false,
        octgnId: "",
        products: [],
        serial: "",
        tcgPlayerId: 0,
        variant: "Non-Foil"
    }
}

export type Card = CardJsonV2 & {
    normalizedEffectText: string | undefined
}

export const defaultCard: CardJsonV2 = {
    cost: 0,
    elements: [],
    isExBurst: false,
    isMultiPlayable: false,
    isSpecial: false,
    jobs: [],
    name: "",
    number: 0,
    opus: 0,
    power: 0,
    rarity: "",
    serial: "",
    categories: [],
    imageId: "",
    isLB: false,
    text: "",
    type: ""
}


export const CARD_GROUP_BY = ["None", "Type"] as const;
export type CardGroupBy = typeof CARD_GROUP_BY[number];

export const CARD_SORT_BY = ["Serial (Desc)", "Cost (Asc)"] as const;
export type CardSortBy = typeof CARD_SORT_BY[number];

export function getDefaultCardTypeSelectorState(): CheckboxButtonGroupValue<CardType>[] {
    return [
        {
            label: "Forward",
            data: "Forward",
            isChecked: false
        },
        {
            label: "Backup",
            data: "Backup",
            isChecked: false
        },
        {
            label: "Monster",
            data: "Monster",
            isChecked: false
        },
        {
            label: "Summon",
            data: "Summon",
            isChecked: false
        }
    ];
}

export type CardFlag = "special" | "exburst" | "generic" | "limitbreak";

export function getDefaultCardFlagSelectorState(): CheckboxButtonGroupValue<CardFlag>[] {
    return [
        {
            label: "Special",
            data: "special",
            isChecked: false
        },
        {
            label: "EX Burst",
            data: "exburst",
            isChecked: false
        },
        {
            label: "Generic",
            data: "generic",
            isChecked: false
        },
        {
            label: "LB",
            data: "limitbreak",
            isChecked: false
        }
    ];
}

export function getDefaultCardCostSelectorState(): CheckboxButtonGroupValue<number>[] {
    return [
        { label: "1", data: 1 },
        { label: "2", data: 2 },
        { label: "3", data: 3 },
        { label: "4", data: 4 },
        { label: "5", data: 5 },
        { label: "6", data: 6 },
        { label: "7", data: 7 },
        { label: "8", data: 8 },
        { label: "9", data: 9 },
        { label: "10", data: 10 },
        { label: "11", data: 11 },
    ]
}

export function getDefaultTextSymbolSelectorState(): CheckboxButtonGroupValue<TextTag>[] {
    return [
        {
            label: "Dull Symbol",
            data: TEXT_TAGS.dullIcon,
            isChecked: false
        },
        {
            label: "Crystal Symbol",
            data: TEXT_TAGS.crystalIcon,
            isChecked: false
        },
        {
            label: "Zero Cost Symbol",
            data: TEXT_TAGS.zeroCostIcon,
            isChecked: false
        },
        {
            label: "Any Cost Symbol",
            data: TEXT_TAGS.anyCostIcon,
            isChecked: false
        },
        {
            label: "Fire Symbol",
            data: TEXT_TAGS.fireIcon,
            isChecked: false
        },
        {
            label: "Ice Symbol",
            data: TEXT_TAGS.iceIcon,
            isChecked: false
        },
        {
            label: "Wind Symbol",
            data: TEXT_TAGS.windIcon,
            isChecked: false
        },
        {
            label: "Earth Symbol",
            data: TEXT_TAGS.earthIcon,
            isChecked: false
        },
        {
            label: "Lightning Symbol",
            data: TEXT_TAGS.lightningIcon,
            isChecked: false
        },
        {
            label: "Water Symbol",
            data: TEXT_TAGS.waterIcon,
            isChecked: false
        },
    ];
}

export function getDefaultRaritySelectorState(): CheckboxButtonGroupValue<CardSerialType>[] {
    return [
        {
            label: "C",
            data: "C",
            isChecked: false
        },
        {
            label: "R",
            data: "R",
            isChecked: false
        },
        {
            label: "H",
            data: "H",
            isChecked: false
        },
        {
            label: "L",
            data: "L",
            isChecked: false
        },
        {
            label: "S",
            data: "S",
            isChecked: false
        },
        {
            label: "PR",
            data: "PR",
            isChecked: false
        }
    ];
}